import { List } from "@mui/material";

import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { getUserById } from "@/api/auth/actions";
import { useSidebarMenus } from "@/hooks/useSidebarMenus";
import { hasPendingWorkspaceSelection } from "@/hooks/useSignupProvisioning";
import { logger } from "@/lib/logger";
import { rootReducerName } from "@/store/rootReducer";
import { hasVisibleMenuItem } from "@/utils/menuAccess";
import { mergePermissionsFromGroups } from "@/utils/permissions";
import { useRouter } from "next/router";

// import NestedItem from "./MenuItem";
import NestedItemNew from "./MenuItemNew";

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const maybeError = error as { name?: string; code?: number; message?: string };
  return (
    maybeError.name === "AbortError" ||
    maybeError.code === 20 ||
    maybeError.message === "AbortError" ||
    maybeError.message === "The operation was aborted."
  );
};

function NestedList() {
  // Always use the dynamic menu hook for up-to-date feature flag state
  const menus = useSidebarMenus();
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [currentPath, setCurrentPath] = useState("");
  const router = useRouter();
  const user = useSelector((state) => state[rootReducerName.auth]?.user);
  const activePermissionsLoadRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setCurrentPath(router.asPath);
  }, [router.asPath]);

  useEffect(() => {
    activePermissionsLoadRef.current?.abort();
    activePermissionsLoadRef.current = null;

    let cancelled = false;

    if (!user) {
      // dispatch(togglePopup());
      logger.warn("No user found at components/Menu ");
      setUserPermissions([]);
      return () => {
        cancelled = true;
      };
    }

    if (hasPendingWorkspaceSelection(user)) {
      setUserPermissions(mergePermissionsFromGroups(user.groups || []));
      return () => {
        cancelled = true;
      };
    }

    const controller = new AbortController();
    activePermissionsLoadRef.current = controller;

    const fetchPermissions = async () => {
      try {
        const res: any = await getUserById(user.id, undefined, {
          suppressAuthLogoutOn401: true,
          signal: controller.signal
        });
        if (controller.signal.aborted) {
          return;
        }
        const profilePermissions = mergePermissionsFromGroups(res?.content?.groups || []);
        if (!cancelled) {
          setUserPermissions(profilePermissions);
        }
        return;
      } catch (error) {
        if (controller.signal.aborted || isAbortError(error)) {
          return;
        }
        logger.error("Error fetching user data for menu permissions:", error);
      }

      if (!cancelled) {
        setUserPermissions(mergePermissionsFromGroups(user.groups || []));
      }
    };

    void fetchPermissions();

    return () => {
      cancelled = true;
      controller.abort();
      if (activePermissionsLoadRef.current === controller) {
        activePermissionsLoadRef.current = null;
      }
    };
  }, [user?.id]);

  return (
    <List component="div">
      {menus.map((menu, index) => {
        const isVisible = hasVisibleMenuItem(menu, { user, userPermissions });

        return (
          <div key={"menu_" + (index + 1)}>
            {isVisible && (
              <NestedItemNew
                itemKey={"menu_" + (index + 1)}
                menu={menu}
                level={1}
                currentPath={currentPath}
                userPermissions={userPermissions}
              />
            )}
          </div>
        );
      })}
    </List>
  );
}

export default NestedList;
